/* |

Port of: https://hackage.haskell.org/package/maximal-cliques
Port author             :  Adam Szlachta
Original Haskell author :  Gershom Bazerman

Haskell package information:

module      :  Data.Algorithm.MaximalCliques
Copyright   :  (c) Gershom Bazerman, 2010
License     :  BSD 3 Clause
Maintainer  :  gershomb@gmail.com
Stability   :  experimental

This library uses the Bron-Kerbosch algorithm to enumerate all maximal cliques in an undirected graph.
A clique is a set of nodes such that there is an edge between every node and every other node in the set.
A maximal clique is a clique such that no node may be added while preserving the clique property.

Maximal clique enumeration is ExpTime complete, and even finding the greatest single clique
(the maximum clique) is NP-complete. The Bron-Kerbosch algorithm is known to run well in practice
while maintaining a simple implementation. If more efficiency is desired, there are now better algorithms.
See, for example, Makino and Uno: <http://citeseerx.ist.psu.edu/viewdoc/summary?doi=10.1.1.138.705>.
Patches providing improved implementations are more than welcome.

*/
import * as $runtime from "../runtime.js";
import * as Data$dArray from "../Data.Array/index.js";
import * as Data$dFoldable from "../Data.Foldable/index.js";
import * as Data$dFunctor from "../Data.Functor/index.js";
import * as Data$dList from "../Data.List/index.js";
import * as Data$dMap$dInternal from "../Data.Map.Internal/index.js";
import * as Data$dOrd from "../Data.Ord/index.js";
import * as Data$dSemigroup from "../Data.Semigroup/index.js";
import * as Data$dSet from "../Data.Set/index.js";
import * as Data$dTraversable from "../Data.Traversable/index.js";
import * as Data$dTraversable$dAccum$dInternal from "../Data.Traversable.Accum.Internal/index.js";
import * as Data$dTuple from "../Data.Tuple/index.js";
import * as Data$dUnfoldable from "../Data.Unfoldable/index.js";
const mapAccumLTuple = dictTraversable => f => s => xs => {
  const $0 = dictTraversable.traverse(Data$dTraversable$dAccum$dInternal.applicativeStateL)(a => s$1 => {
    const $0 = f(s$1)(a);
    return {accum: $0._1, value: $0._2};
  })(xs)(s);
  return Data$dTuple.$Tuple($0.accum, $0.value);
};
const maximalCliques = pickpivot => neighborsOf => nodeset => {
  const go = r => p => x => {
    if (p.tag === "Leaf" && x.tag === "Leaf") { return [r]; }
    return Data$dArray.concat(mapAccumLTuple(Data$dTraversable.traversableArray)(v => v1 => {
      const nv = neighborsOf(v1);
      return Data$dTuple.$Tuple(
        Data$dTuple.$Tuple(Data$dMap$dInternal.delete(Data$dOrd.ordInt)(v1)(v._1), Data$dMap$dInternal.insert(Data$dOrd.ordInt)(v1)()(v._2)),
        go(Data$dMap$dInternal.insert(Data$dOrd.ordInt)(v1)()(r))(Data$dSet.intersection(Data$dOrd.ordInt)(nv)(v._1))(Data$dSet.intersection(Data$dOrd.ordInt)(nv)(v._2))
      );
    })(Data$dTuple.$Tuple(p, x))(Data$dList.toUnfoldable(Data$dUnfoldable.unfoldableArray)(Data$dMap$dInternal.keys(Data$dSet.difference(Data$dOrd.ordInt)(p)(neighborsOf(pickpivot(p)(x))))))._2);
  };
  return go(Data$dMap$dInternal.Leaf)(nodeset)(Data$dMap$dInternal.Leaf);
};
const getMaximalCliques = tolFun => xs => {
  const lnodes = Data$dArray.zip(Data$dArray.range(0)(Data$dFoldable.foldlArray(c => v => 1 + c | 0)(0)(xs)))(xs);
  const lv = Data$dArray.fromFoldableImpl(Data$dFoldable.foldrArray)(Data$dFunctor.arrayMap(v => {
    const $0 = v._1;
    return Data$dTuple.$Tuple(
      v._2,
      Data$dFoldable.foldlArray(m => a => Data$dMap$dInternal.insert(Data$dOrd.ordInt)(a)()(m))(Data$dMap$dInternal.Leaf)(Data$dArray.filter(v1 => v1 !== $0)(Data$dFunctor.arrayMap(Data$dTuple.fst)(Data$dArray.filter((() => {
        const $1 = tolFun(v._2);
        return x => $1(x._2);
      })())(lnodes))))
    );
  })(lnodes));
  return Data$dFunctor.arrayMap((() => {
    const $0 = Data$dFunctor.arrayMap(x => {
      const $0 = Data$dArray.index(lv)(x);
      if ($0.tag === "Just") { return $0._1._1; }
      $runtime.fail();
    });
    const $1 = Data$dArray.fromFoldableImpl(Data$dSet.foldableSet.foldr);
    return x => $0($1(x));
  })())(maximalCliques(p => x => {
    const $0 = Data$dArray.index(Data$dSemigroup.concatArray(Data$dList.toUnfoldable(Data$dUnfoldable.unfoldableArray)(Data$dMap$dInternal.keys(p)))(Data$dList.toUnfoldable(Data$dUnfoldable.unfoldableArray)(Data$dMap$dInternal.keys(x))))(0);
    if ($0.tag === "Just") { return $0._1; }
    $runtime.fail();
  })(x => {
    const $0 = Data$dArray.index(lv)(x);
    if ($0.tag === "Just") { return $0._1._2; }
    $runtime.fail();
  })(Data$dFoldable.foldlArray(m => a => Data$dMap$dInternal.insert(Data$dOrd.ordInt)(a)()(m))(Data$dMap$dInternal.Leaf)(Data$dFunctor.arrayMap(Data$dTuple.fst)(lnodes))));
};
export {getMaximalCliques, mapAccumLTuple, maximalCliques};
